import { useMutation } from "@apollo/client";
import { Dialog, Classes, Button, MenuItem, FormGroup, InputGroup, Checkbox } from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import React, { useCallback, useContext, useState } from "react";
import { UPSERT_ACTION } from "graphql/mutations";
import { GET_POLICY_ACTIONS } from "graphql/queries";
import { PolicyAction } from "types";
import { SelectedProjectContext } from "utils/context";
import { ActionToaster } from "utils/toasters";

type Props = {
    toggle: () => void;
    isOpen: boolean;
    action?: PolicyAction;
    buttonText: "Create" | "Edit";
};

export default function ActionsDialog({ toggle, isOpen, action, buttonText }: Props) {
    const [project] = useContext(SelectedProjectContext);
    const [name, setName] = useState("");
    const [ciQuery, setCiQuery] = useState("");
    const [type, setType] = useState<ActionType>(typeItems[0]);
    const [severities, setSeverities] = useState<number[]>([]);
    const [upsertAction] = useMutation<PolicyAction>(UPSERT_ACTION);
    const [loading, setLoading] = useState(false);

    const [timeWindow, setTimeWindow] = useState("");
    const [numberOfIssues, setNumberOfIssues] = useState("");
    const [triggerExpiration, setTriggerExpiration] = useState("");
    const [includedResources, setIncludedResources] = useState<string[]>([]);
    const [ignoreResource, setIgnoreResource] = useState(false);

    const onOpen = useCallback(() => {
        if (action) {
            const type = action.type === "remediationaction" ? typeItems[1] : typeItems[0];
            setName(action.name || "");
            setType(type);
            setCiQuery(action.filter.includedCiQuery);
            setSeverities(action.filter.severity);
            if (action.evaluationPolicy) {
                setTimeWindow(action.evaluationPolicy.timeWindow.toString());
                setNumberOfIssues(action.evaluationPolicy.numberOfIssues.toString());
                setTriggerExpiration(action.triggerExpirationTimeSeconds?.toString() || "");
                setIgnoreResource(action.ignoreResourceOnTrigger);
                setIncludedResources(action.filter.includedResources ?? []);
            }
        }
    }, [action]);

    const handleSubmit = useCallback(() => {
        if (project) {
            const actionInput: PolicyAction = {
                id: action?.id,
                name: name,
                project: {
                    id: project.projectId,
                    name: project.name
                },
                filter: {
                    severity: severities,
                    includedCiQuery: ciQuery,
                    includedCis: [],
                    includedResources: includedResources
                },
                roles: type.value === "alarmaction" ? [] : undefined,
                evaluationPolicy:
                    type.value === "remediationaction"
                        ? {
                              timeWindow: timeWindow ? +timeWindow : 36000,
                              numberOfIssues: numberOfIssues ? +numberOfIssues : 1
                          }
                        : undefined,
                //todo: why is this no being checked and being applied to alarm actions automatically?
                triggerExpirationTimeSeconds: triggerExpiration ? +triggerExpiration : 30,
                ignoreResourceOnTrigger: ignoreResource
            } as PolicyAction;
            setLoading(true);
            upsertAction({
                variables: {
                    type: type.value,
                    action: actionInput
                },
                refetchQueries: [
                    {
                        query: GET_POLICY_ACTIONS,
                        variables: { projectId: project.projectId }
                    }
                ]
            })
                .then((result) => {
                    setLoading(false);
                    ActionToaster.show({
                        message: `Successfully ${buttonText}ed  Action`,
                        intent: "success"
                    });
                    setTimeout(toggle, 1000);
                })
                .catch((error) => {
                    console.log("error: ", error);
                    setLoading(false);
                    ActionToaster.show({
                        message: `Failed to ${buttonText}  Action`,
                        intent: "danger"
                    });
                    setTimeout(toggle, 1000);
                });
        }
    }, [
        action,
        buttonText,
        ciQuery,
        ignoreResource,
        name,
        numberOfIssues,
        project,
        includedResources,
        severities,
        timeWindow,
        toggle,
        triggerExpiration,
        type.value,
        upsertAction
    ]);

    return (
        <Dialog
            icon="new-object"
            onClose={toggle}
            title={`${buttonText} Action for Project: ${project?.name}`}
            isOpen={isOpen}
            onOpening={onOpen}
        >
            <div className={Classes.DIALOG_BODY}>
                <FormGroup label="Name" labelInfo="*" labelFor="text-input">
                    <InputGroup
                        placeholder="Name for the Action"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    ></InputGroup>
                </FormGroup>
                <FormGroup label="Type" labelFor="text-input">
                    <TypeSelect
                        //disabled={!name}
                        filterable={false}
                        itemRenderer={renderType}
                        items={typeItems}
                        activeItem={type}
                        onItemSelect={(t) => {
                            setType(t);
                        }}
                        popoverProps={{ minimal: true }}
                    >
                        <Button
                            //disabled={!name}
                            rightIcon="caret-down"
                            text={type.text}
                        />
                    </TypeSelect>
                </FormGroup>
                <FormGroup label="CI Query" labelFor="text-input">
                    <InputGroup
                        placeholder="Include CI Query"
                        value={ciQuery}
                        onChange={(e) => setCiQuery(e.target.value)}
                    ></InputGroup>
                </FormGroup>
                <FormGroup label="Severity">
                    {severityOptions.map((s) => (
                        <Checkbox
                            checked={severities.includes(s.value)}
                            label={s.text}
                            inline
                            onChange={() => {
                                const set = new Set(severities);
                                if (set.has(s.value)) {
                                    set.delete(s.value);
                                } else {
                                    set.add(s.value);
                                }

                                setSeverities(Array.from(set));
                            }}
                        />
                    ))}
                </FormGroup>

                {type.value === "remediationaction" && (
                    <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", flex: "1" }}>
                        <FormGroup label="Time Window" labelFor="text-input">
                            <InputGroup
                                placeholder="# of Seconds"
                                value={timeWindow.toString()}
                                onChange={(e) => {
                                    if (!/\D/.test(e.target.value)) {
                                        setTimeWindow(e.target.value);
                                    }
                                }}
                            ></InputGroup>
                        </FormGroup>
                        <FormGroup label="Number of Issues" labelFor="text-input">
                            <InputGroup
                                placeholder="# of Issues in Window"
                                value={numberOfIssues.toString()}
                                onChange={(e) => {
                                    if (!/\D/.test(e.target.value)) {
                                        setNumberOfIssues(e.target.value);
                                    }
                                }}
                            ></InputGroup>
                        </FormGroup>
                        <FormGroup label="Expiration of Trigger" labelFor="text-input">
                            <InputGroup
                                placeholder="# of Seconds"
                                value={triggerExpiration.toString()}
                                onChange={(e) => {
                                    if (!/\D/.test(e.target.value)) {
                                        setTriggerExpiration(e.target.value);
                                    }
                                }}
                            ></InputGroup>
                        </FormGroup>
                        <FormGroup label="Resources" labelFor="text-input">
                            <InputGroup
                                placeholder="Comma delimited list of resources"
                                value={includedResources.toString()}
                                onChange={(e) => {
                                    setIncludedResources(e.target.value.split(","));
                                }}
                            ></InputGroup>
                        </FormGroup>
                        <Checkbox
                            checked={ignoreResource}
                            label="Trigger on any Resource"
                            onChange={() => setIgnoreResource(!ignoreResource)}
                        />
                    </div>
                )}
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={buttonText}
                        intent={buttonText === "Create" ? "primary" : "warning"}
                        disabled={!name}
                        loading={loading}
                        onClick={handleSubmit}
                    ></Button>
                </div>
            </div>
        </Dialog>
    );
}

type ActionType = { text: string; value: string };

const typeItems: ActionType[] = [
    {
        text: "Alarm Action",
        value: "alarmaction"
    },
    {
        text: "Remediation Action",
        value: "remediationaction"
    }
];

const TypeSelect = Select.ofType<ActionType>();

const renderType: ItemRenderer<ActionType> = (type, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }

    return <MenuItem active={modifiers.active} key={type.value} onClick={handleClick} text={type.text} />;
};

const severityOptions = [
    {
        text: "Critical",
        value: 1,
        checked: false
    },
    {
        text: "Major",
        value: 2,
        checked: false
    },
    {
        text: "Minor",
        value: 3,
        checked: false
    },
    {
        text: "Warning",
        value: 4,
        checked: false
    },
    {
        text: "Clear",
        value: 0,
        checked: false
    }
];
